import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useTranslation } from "react-i18next";
import { ErrorBoundary, CtyButton, CtyShowToast, checkType, SimpleDeleteConfirmationModal, useAgGridStatePersistence, useHasApplicationResource, PageHeader, CustomTabs, AuditLogsAgGridWrapper, CREDENTIAL_AUDIT_LOG_STATIC_FILTERS } from "@circuitry-ai/doc-ui";
import { Icon } from '@iconify/react';
import { Spinner, Tooltip } from '@nextui-org/react';
import CredentialModal from './credentialModal';
import { 
    Credential,
    useGetCredentialsListQuery, 
    useDeleteCredentialMutation
} from '@circuitry-ai/doc-data';
import { ColDef, GridReadyEvent } from 'ag-grid-community';

const CredentialModalWithErrorBoundary = ErrorBoundary(CredentialModal);
const SimpleDeleteConfirmationModalWithErrorBoundary = ErrorBoundary(SimpleDeleteConfirmationModal as unknown as React.ComponentType<Record<string, unknown>>);
const PageHeaderWithErrorBoundary = ErrorBoundary(PageHeader);

export default function CredentialsSettings() {
    const { t } = useTranslation();
    const [selectedTab, setSelectedTab] = useState('credentials');
    const hasCredentialsAccess = useHasApplicationResource('credentials');
    const [showCredentialModal, setShowCredentialModal] = useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [currentCredentialId, setCurrentCredentialId] = useState<string | undefined>(undefined);
    const [currentActiveItem, setCurrentActiveItem] = useState<number>(-1);
    const [currentCredential, setCurrentCredential] = useState<Credential | null>(null);
    const [rowData, setRowData] = useState<Credential[]>([]);

    const { data: credentialsList, isLoading: isCredentialsListLoading, refetch: refetchCredentialsList } = useGetCredentialsListQuery(undefined, { 
        refetchOnMountOrArgChange: true 
    });
    const [deleteCredential, { isLoading: isDeletingCredential }] = useDeleteCredentialMutation();

    useEffect(() => {
        if (credentialsList) {
            // Handle both array and object with data property
            setRowData(credentialsList);
        }
    }, [credentialsList]);

    const gridStyle = useMemo(() => ({
        height: "100%",
        width: "100%",
        '--ag-border-color': '#e0e0e0',
        '--ag-cell-horizontal-border': '1px solid var(--ag-border-color)',
    } as React.CSSProperties), []);

    const paginationPageSizeSelector = useMemo(() => {
        return [10, 20, 40, 50];
    }, []);

    function actionsCellRenderer(params: { data: Credential, rowIndex: number }) {
        return (
            <div className="flex justify-center gap-3">
                <Tooltip size="sm" color="success" content={t("Edit")}>
                    <span className={`text-primary pt-3 ${hasCredentialsAccess ? 'cursor-pointer active:opacity-50' : 'opacity-50 cursor-not-allowed'}`}>
                        <Icon icon="tabler:edit" width="16" height="16" onClick={() => hasCredentialsAccess && handleFieldEdit(params.data)} />
                    </span>
                </Tooltip>
                <Tooltip size="sm" color="danger" content={t("Delete")}>
                    <span className={`relative text-danger pt-3 ${hasCredentialsAccess ? 'cursor-pointer active:opacity-50' : 'opacity-50 cursor-not-allowed'}`}>
                        {(isDeletingCredential && currentActiveItem === params.rowIndex) && (
                            <Spinner className="absolute left-2 -bottom-6 transform -translate-x-1/2 -translate-y-1/2" />
                        )}
                        <Icon icon="hugeicons:delete-02" width="16" height="16" onClick={() => hasCredentialsAccess && handleFieldDelete(params.data, params.rowIndex)} />
                    </span>
                </Tooltip>
            </div>
        );
    }

    const handleFieldEdit = (credential: Credential) => {
        const credentialId = credential?.credential_id;
        if (credentialId) {
            setCurrentCredentialId(credentialId);
            setShowCredentialModal(true);
        }
    };

    const handleFieldDelete = (credential: Credential, idx: number) => {
        const credentialId = credential?.credential_id;
        if (credentialId) {
            setCurrentActiveItem(idx);
            setCurrentCredentialId(credentialId);
            setCurrentCredential(credential);
            setShowDeleteModal(true);
        }
    };

    const handleDeleteConfirm = async (credential: Credential) => {
        const credentialId = credential?.credential_id;
        if (!credentialId) return;

        try {
            const response = await deleteCredential(credentialId).unwrap();
            CtyShowToast(checkType.success, response?.message || t('Credential deleted successfully'));
            await refetchCredentialsList();
            setShowDeleteModal(false);
            setCurrentCredentialId(undefined);
            setCurrentCredential(null);
            setCurrentActiveItem(-1);
        } catch (error) {
            CtyShowToast(checkType.error, (error as Error)?.message || t('Error deleting credential'));
            setCurrentActiveItem(-1);
        }
    };

    const handleCredentialSuccess = async () => {
        await refetchCredentialsList();
    };

    const handleNewCredential = () => {
        setCurrentCredentialId(undefined);
        setShowCredentialModal(true);
    };

    const handleModalClose = () => {
        setShowCredentialModal(false);
        setCurrentCredentialId(undefined);
    };

    const columnDefs = useMemo(() => [
        {
            headerName: t('Credential Name'),
            field: 'name',
            sortable: true,
            flex: 1,
            minWidth: 200
        },
        {
            headerName: t('Description'),
            field: 'description',
            sortable: true,
            flex: 1,
            minWidth: 200,
            cellRenderer: (params: { value: string }) => {
                const description = params?.value ?? '';
                return description?.length > 50 ? `${description.substring(0, 50)}...` : description;
            }
        },
        {
            headerName: t('Credential Type'),
            field: 'credential_type',
            sortable: true,
            minWidth: 150
        },
        {
            headerName: t('Actions'),
            field: 'actions',
            sortable: false,
            minWidth: 150,
            pinned: 'right',
            lockPinned: true,
            lockPosition: true,
            cellRenderer: (params: { data: Credential, rowIndex: number }) => actionsCellRenderer(params),
        }
    ] as ColDef[], [t]);

    const defaultColDef = useMemo(() => ({
        resizable: true,
    }), []);

    const { augmentGridReady, mergeColumnDefs, suppressDragLeaveHidesColumns } = useAgGridStatePersistence('credentials-settings-table');

    const onGridReady = useCallback((params: GridReadyEvent) => {
        augmentGridReady(params, (p) => {
            p?.api?.sizeColumnsToFit?.();
        });
    }, [augmentGridReady]);

    const columnDefsWithSort = useMemo(() => mergeColumnDefs(columnDefs), [columnDefs, mergeColumnDefs]);

    function renderCredentialsGrid() {
        if (isCredentialsListLoading) {
            return (
                <div className="flex justify-center items-center h-64">
                    <Spinner size="lg" />
                </div>
            );
        }

        return (
            <div className={'ag-theme-alpine mt-2'} style={gridStyle}>
                <AgGridReact
                    suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
                    rowData={rowData || []}
                    columnDefs={columnDefsWithSort}
                    defaultColDef={defaultColDef}
                    headerHeight={40}
                    domLayout="autoHeight"
                    onGridReady={onGridReady}
                    pagination={true}
                    paginationPageSize={10}
                    paginationPageSizeSelector={paginationPageSizeSelector}
                    alwaysShowHorizontalScroll={true}
                />
            </div>
        );
    }


    return (
        <div className="flex flex-col gap-2 h-full min-h-0">
            <PageHeaderWithErrorBoundary
                heading={t('Integration Credentials')}
                description={t('Manage integration credentials and track activity')}
                pageHeaderClass="!px-0 !gap-1 !pb-0"
            />

            <CustomTabs
                tabs={[
                    { key: 'credentials', title: t('Credentials'), id: 'credentials-tab' },
                    { key: 'audit-logs', title: t('Audit Logs'), id: 'audit-logs-tab' },
                ]}
                selectedKey={selectedTab}
                onTabChange={setSelectedTab}
            />
            <div className="pt-4">
                {selectedTab === 'credentials' && (
                    <>
                        <div className="flex justify-between items-center">
                            <div className="font-semibold">{t('Credentials Management')}</div>
                            <CtyButton 
                                id="new-credential" 
                                size="sm" 
                                className="text-primary font-bold" 
                                variant="bordered" 
                                color="primary"
                                onPress={handleNewCredential}
                                startContent={<Icon icon="fa6-solid:plus" />}
                                isDisabled={!hasCredentialsAccess}
                            >
                                {t('New Credential')}
                            </CtyButton>
                        </div>
                        <div>{renderCredentialsGrid()}</div>
                    </>
                )}
                {selectedTab === 'audit-logs' && (
                    <AuditLogsAgGridWrapper staticFilters={CREDENTIAL_AUDIT_LOG_STATIC_FILTERS} />
                )}
            </div>
            {showCredentialModal && (
                <CredentialModalWithErrorBoundary
                    showModal={showCredentialModal}
                    onDialogClose={handleModalClose}
                    credentialId={currentCredentialId}
                    onSuccess={handleCredentialSuccess}
                />
            )}
            {showDeleteModal && currentCredential && (
                <SimpleDeleteConfirmationModalWithErrorBoundary
                    isOpen={showDeleteModal}
                    onOpenChange={(open: boolean) => {
                        if (!open) {
                            setShowDeleteModal(false);
                            setCurrentCredential(null);
                            setCurrentCredentialId(undefined);
                        }
                    }}
                    isDismissable={false}
                    entityName={currentCredential?.name || ''}
                    onConfirm={() => void handleDeleteConfirm(currentCredential)}
                    isConfirmLoading={isDeletingCredential}
                    cancelLabel={t('Close')}
                    confirmLabel={t('Delete Credential')}
                    cancelButtonTestId="deleteModal-cancel-btn"
                    confirmButtonTestId="deleteModal-delete-btn"
                />
            )}
        </div>
    );
}
